import React, { createRef, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import DotLoader from '@svg/dot_loader';

import { Send } from '@mui/icons-material';
import { Message } from '@common/Node/CompletionChatbot/ChatMessage/Message';
import { Select } from '@common/Select';
import { MessageType } from '@common/hooks/useChatbot';
import { Dialog as MUIDialog, DialogContent, IconButton, InputBase, Stack } from '@mui/material';
import { ChatTool } from './ChatTool';

const Dialog = styled(MUIDialog)`
  & .MuiDialog-paper {
    height: 85vh;
    max-width: 90vw;
    background-color: ${({ theme }) => theme.background.standard} !important;
  }

  & .MuiDialogContent-root {
    padding: 0;
    display: flex;
    flex-direction: column;
    height: 100%;
  }
`;

const Container = styled.div<{ hasTools: boolean }>`
  display: grid;
  grid-template-columns: ${({ hasTools }) => (hasTools ? '7fr 4fr' : '1fr')};
  height: 100%;
  overflow: hidden;
`;

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-width: 0;
  height: 100%;
  overflow: hidden;

  border-right: 1px solid ${({ theme }) => theme.line.light};

  ::-webkit-scrollbar {
    width: 14px;
    height: 14px;
  }

  ::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.scrollbar.track};
  }

  ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.scrollbar.thumb};
  }
`;

const SidebarContainer = styled.div`
  padding: 8px 16px;
  overflow-y: auto;
`;

const MessageContainer = styled.div`
  flex: 1;
  word-break: break-word;
  overflow-y: auto;

  ::-webkit-scrollbar {
    width: 14px;
    height: 14px;
  }

  ::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.scrollbar.track};
  }

  ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.scrollbar.thumb};
  }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  padding: 1rem;
  border-top: 1px solid ${({ theme }) => theme.line.light};
`;

const Action = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 22px;
  width: 22px;
`;

const ChatInput = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  background-color: ${({ theme }) => theme.background.standard};

  svg {
    color: ${({ theme }) => theme.text.standard};
  }
`;

const VariableInput = styled.div`
  display: flex;
  margin-bottom: 1rem;
  width: 90%;
  margin-top: 1rem;
  justify-content: center;
`;

const ToolContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
`;

interface Props {
  openTool: string | null;
  setExpandModalOpen: React.Dispatch<React.SetStateAction<string | null>>;
  messages: MessageType[];
  input: string;
  setInput: React.Dispatch<React.SetStateAction<string>>;
  sendMessage: () => Promise<void>;
  executionRunning: boolean;
  chatVariable: string;
  setChatVariable: React.Dispatch<React.SetStateAction<string>>;
  traceVariables: string[];
  hasTools: boolean;
}

export const ChatExpandModal: React.FC<Props> = ({
  openTool,
  setExpandModalOpen,
  messages,
  input,
  setInput,
  sendMessage,
  executionRunning,
  chatVariable,
  setChatVariable,
  traceVariables,
  hasTools,
}) => {
  const [alt, setAlt] = useState(false);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const bottomRef = createRef<HTMLDivElement>();
  const tools = messages?.map((message) => message.events.functions).flat();

  const open = Boolean(openTool);

  useEffect(() => {
    if (open) {
      const timeout = setTimeout(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 300);

      return () => clearTimeout(timeout);
    }
  }, [open, messages]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={() => setExpandModalOpen(null)} fullWidth maxWidth='xl'>
      <DialogContent>
        <Container hasTools={hasTools}>
          <ChatContainer>
            <MessageContainer>
              {messages.map((message, index) => (
                <Message key={index} entry={message} setExpandModalOpen={setExpandModalOpen} />
              ))}
              <div ref={bottomRef} />
            </MessageContainer>
            <Footer>
              <ChatInput>
                <InputBase
                  inputRef={inputRef}
                  maxRows={8}
                  multiline
                  onKeyDown={(e) => {
                    if (e.key === 'Control' || e.key === 'Shift') return setAlt(true);
                    if (e.key === 'Enter' && alt) return;
                    if (e.key !== 'Enter') return;
                    if (input.trim() === '') return;

                    e.preventDefault();
                    e.stopPropagation();

                    sendMessage();
                  }}
                  onKeyUp={(e) => {
                    if (e.key === 'Control' || e.key === 'Shift') return setAlt(false);
                  }}
                  sx={{ ml: 1, flex: 1 }}
                  placeholder='Send a message...'
                  value={input}
                  onChange={(e) => setInput(e?.target?.value)}
                />
                <Stack direction='row' spacing={'2px'}>
                  {executionRunning ? (
                    <IconButton disabled>
                      <Action>
                        <DotLoader height={6} />
                      </Action>
                    </IconButton>
                  ) : (
                    <IconButton onClick={sendMessage}>
                      <Action>
                        <Send sx={{ fontSize: '1.4ch' }} />
                      </Action>
                    </IconButton>
                  )}
                </Stack>
              </ChatInput>
              {traceVariables.length > 1 && (
                <VariableInput>
                  <Select
                    value={chatVariable}
                    setValue={setChatVariable}
                    options={traceVariables.map((item) => ({
                      label: item.replace('http.request.', ''),
                      value: item,
                    }))}
                  />
                </VariableInput>
              )}
            </Footer>
          </ChatContainer>
          {hasTools && (
            <SidebarContainer>
              <ToolContainer>
                {tools.reverse().map((tool, index) => (
                  <ChatTool
                    key={`${tool.action}-${index}`}
                    tool={tool}
                    openTool={openTool}
                    setOpenTool={setExpandModalOpen}
                  />
                ))}
              </ToolContainer>
            </SidebarContainer>
          )}
        </Container>
      </DialogContent>
    </Dialog>
  );
};
